import useActivityTimeline from "@/modules/lead-page/new/hooks/activity-timline.hook";
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { useActivityContext } from "./activity-context.hook";
import { queryClient } from "@/queryClient";
import { useParams } from "react-router-dom";
import { getDistanceFromBottom } from "../utils/scroll.utils";

const useActivityScroll = () => {
  const loaderTopRef = useRef<HTMLDivElement | null>(null);
  const loaderBottomRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledRef = useRef(false);
  const skipAutoScrollRef = useRef(false);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const previousMostRecentItemIdRef = useRef<string | null>(null);
  const snapshotRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
    count: number;
  } | null>(null);
  const { lead_id } = useParams<{ lead_id: string }>();

  const {
    allItems,
    mostRecentActivityItem,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
  } = useActivityTimeline();

  const {
    forceRefetch,
    highlightedActivityId,
    setHighlightedActivityId,
    isFindingActivity,
    findInActivityRoute,
    setFindInActivityRoute,
  } = useActivityContext();

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setContainerEl(node);
    }
  }, []);

  // Observer for scrolling up (loading older content at top)
  useEffect(() => {
    if (!containerEl || !loaderTopRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !highlightedActivityId &&
          !isFindingActivity
        ) {
          snapshotRef.current = {
            scrollHeight: containerEl.scrollHeight,
            scrollTop: containerEl.scrollTop,
            count: allItems.length,
          };
          fetchNextPage();
        }
      },
      {
        root: containerEl,
        threshold: 0.1,
      }
    );

    const currentLoader = loaderTopRef.current;
    if (currentLoader) observer.observe(currentLoader);
    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [
    containerEl,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    allItems.length,
    highlightedActivityId,
    isFindingActivity,
  ]);

  useLayoutEffect(() => {
    if (!containerEl || !snapshotRef.current) return;

    const {
      scrollHeight: prevScrollHeight,
      scrollTop: prevScrollTop,
      count: prevCount,
    } = snapshotRef.current;

    if (allItems.length > prevCount) {
      const newScrollHeight = containerEl.scrollHeight;
      const heightDiff = newScrollHeight - prevScrollHeight;

      if (heightDiff > 0) {
        containerEl.scrollTop = prevScrollTop + heightDiff;
      }
      snapshotRef.current = null;
    }
  }, [allItems.length, containerEl]);

  // Observer for scrolling down (loading newer content at bottom)
  useEffect(() => {
    if (!containerEl || !loaderBottomRef.current) return;

    const currentLoader = loaderBottomRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          hasPreviousPage &&
          !isFetchingPreviousPage
        ) {
          // Prevent auto-scroll to bottom when loading more items via pagination
          skipAutoScrollRef.current = true;
          fetchPreviousPage?.();
        }
      },
      {
        root: containerEl,
        threshold: 0,
      }
    );

    if (currentLoader) {
      observer.observe(currentLoader);

      // Manual check if element is already visible
      // This handles the case where we're at bottom and hasPreviousPage becomes true
      // Use setTimeout with RAF to ensure DOM is fully updated
      setTimeout(() => {
        requestAnimationFrame(() => {
          const rect = currentLoader.getBoundingClientRect();
          const containerRect = containerEl.getBoundingClientRect();
          const isVisible =
            rect.top < containerRect.bottom && rect.bottom > containerRect.top;

          if (isVisible && hasPreviousPage && !isFetchingPreviousPage) {
            skipAutoScrollRef.current = true;
            fetchPreviousPage?.();
          }
        });
      }, 100);
    }

    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [
    containerEl,
    fetchPreviousPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    allItems.length,
    isFindingActivity, // Re-run manual check when finding completes
  ]);

  // Auto-fetch around highlighted activity when finding completes
  const hasFetchedAroundHighlightRef = useRef<string | null>(null);

  useEffect(() => {
    // Only run when:
    // 1. Activity is highlighted
    // 2. Finding is complete (data has been loaded)
    // 3. We haven't already fetched around this highlight
    if (
      !highlightedActivityId ||
      isFindingActivity ||
      hasFetchedAroundHighlightRef.current === highlightedActivityId
    ) {
      // Reset the ref when highlight is cleared
      if (!highlightedActivityId) {
        hasFetchedAroundHighlightRef.current = null;
      }
      return;
    }

    // Mark that we're fetching for this highlight
    hasFetchedAroundHighlightRef.current = highlightedActivityId;

    // Small delay to ensure the highlight is rendered and scrolled to
    setTimeout(() => {
      // Fetch older activities (top) if available
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }

      // Fetch newer activities (bottom) if available
      if (hasPreviousPage && !isFetchingPreviousPage) {
        fetchPreviousPage?.();
      }
    }, 2000);
  }, [
    highlightedActivityId,
    isFindingActivity,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  ]);

  // Auto-scrolls to bottom ONLY on first load (not in find mode).
  useEffect(() => {
    if (!hasScrolledRef.current && containerEl && allItems.length > 0) {
      if (!highlightedActivityId) {
        requestAnimationFrame(() => {
          containerEl.scrollTop = containerEl.scrollHeight;
          hasScrolledRef.current = true;
          setShowScrollToBottom(false);
        });
      } else {
        // If highlighting an activity, mark as scrolled so we don't auto-scroll to bottom later
        hasScrolledRef.current = true;
      }
    }
  }, [containerEl, allItems.length, highlightedActivityId, hasPreviousPage]);

  // Auto-scroll to bottom when new activities are added (e.g., from call action)
  // Skip this behavior when highlighting an activity
  useEffect(() => {
    if (!containerEl || !hasScrolledRef.current || !mostRecentActivityItem)
      return;

    const currentMostRecentItemId = `${mostRecentActivityItem.id}-${mostRecentActivityItem.uid}`;
    const previousMostRecentItemId = previousMostRecentItemIdRef.current;

    // Update the ref immediately to track the latest item
    previousMostRecentItemIdRef.current = currentMostRecentItemId;

    // If a new most recent item was added, auto-scroll to bottom
    // This handles user-initiated actions like phone calls that create new activities
    // Skip if we're doing a manual scroll (button click or comment addition)
    if (
      previousMostRecentItemId &&
      currentMostRecentItemId !== previousMostRecentItemId &&
      !skipAutoScrollRef.current &&
      !highlightedActivityId
    ) {
      const performScroll = () => {
        containerEl.scrollTo({
          top: containerEl.scrollHeight,
          behavior: "smooth",
        });
        setShowScrollToBottom(false);
      };

      // Use a small delay to ensure DOM has updated with the new activity
      // Double RAF ensures the browser has completed both layout and paint
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(performScroll);
        });
      }, 200);
    }

    // Reset the skip flag after processing
    if (skipAutoScrollRef.current) {
      skipAutoScrollRef.current = false;
    }
  }, [containerEl, mostRecentActivityItem, highlightedActivityId]);

  // Show/hide scroll-to-bottom button
  useEffect(() => {
    if (!containerEl) return;

    const handleScroll = () => {
      const distance = getDistanceFromBottom(containerEl);
      setShowScrollToBottom(distance > 100);
    };

    containerEl.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => containerEl.removeEventListener("scroll", handleScroll);
  }, [containerEl]);

  const scrollToBottom = useCallback(() => {
    if (!containerEl) return;
    containerEl.scrollTo({ top: containerEl.scrollHeight, behavior: "smooth" });
    setShowScrollToBottom(false);
    // Clear the find in activity route flag when manually scrolling to bottom
    setFindInActivityRoute?.(false);
  }, [containerEl, setFindInActivityRoute]);

  const scrollToBottomWithRefresh = useCallback(() => {
    if (!lead_id) return;

    // Clear highlighted activity when user manually refreshes
    setHighlightedActivityId?.(undefined);

    // Clear the find in activity route flag
    setFindInActivityRoute?.(false);

    // Skip the auto-scroll effect since we're manually scrolling
    skipAutoScrollRef.current = true;

    // Reset the scroll tracker so it auto-scrolls after refetch
    hasScrolledRef.current = false;

    // Clear all cached data for this lead's activity
    queryClient.removeQueries({
      queryKey: ["lead-activity", lead_id],
      exact: false,
    });

    // Force refetch to get the latest items (will start with first 5 items)
    forceRefetch();

    // Hide the scroll button immediately
    setShowScrollToBottom(false);
  }, [lead_id, forceRefetch, setHighlightedActivityId, setFindInActivityRoute]);

  return {
    containerRef,
    containerEl,
    loaderTopRef,
    loaderBottomRef,
    scrollToBottom,
    scrollToBottomWithRefresh: findInActivityRoute
      ? scrollToBottomWithRefresh
      : scrollToBottom,
    showScrollToBottom,
  };
};

export default useActivityScroll;
